import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Spin, Table } from 'antd';
import { API_ROOT } from '../../config';
import { useTranslation } from 'react-i18next';
import { getNetwork } from '../../utils/getNetwork';
import BasicLayout from '../../components/layout/BasicLayout';
import moment from 'moment';
import styles from './UserActions.module.scss';
import { checkRole } from '../../utils/CheckRoleWithToken';
import { CheckboxFilter } from './components/CheckboxFilter';
import { DateRangeFilter } from './components/DateRangeFilter';

const roles = ['all', 'all1', 'akm'];

const Events = {
  VIEW: 'userActions.eventView',
  CREATE: 'userActions.eventCreate',
  UPDATE: 'userActions.eventUpdate',
  DELETE: 'userActions.eventDelete',
  OPEN_CASE: 'userActions.eventOpenCase',
  LOGIN: 'userActions.eventLogin',
  LOGOUT: 'userActions.eventLogout',
};

const EventObject = {
  serviceName: 'userActions.serviceName',
  serviceCode: 'userActions.serviceCode',
  organization: 'userActions.organization',
  modelOrganization: 'userActions.modelOrganization',
  chapter: 'userActions.chapter',
  category: 'userActions.category',
  subcategory: 'userActions.subcategory',
  reportTitle: 'userActions.reportTitle',
  region: 'userActions.region',
  year: 'userActions.year',
  month: 'userActions.month',
};

const EMPTY_FILTERS = {
  username: [],
  dateTime: [],
  event: [],
  caseCode: [],
};

export const UserActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [userActionsData, setUserActionsData] = useState([]);
  const [isAction, setIsAction] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { t } = useTranslation();

  const getData = () => {
    setIsLoading(true);
    getNetwork()
      .get(API_ROOT + '/user-activity')
      .then(res => {
        if (res && res.status === 200) {
          setUserActionsData(res.data);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const filteredData = useMemo(() => {
    return userActionsData.filter(item => {
      if (filters.username.length && !filters.username.includes(item.username)) {
        return false;
      }
      if (filters.event.length && !filters.event.includes(item.event)) {
        return false;
      }
      if (filters.caseCode.length && !filters.caseCode.includes(item.caseCode)) {
        return false;
      }
      if (filters.dateTime.length === 2) {
        const [start, end] = filters.dateTime;
        const startOfDay = moment(start).startOf('day');
        const endOfDay = moment(end).endOf('day');
        const itemTime = moment(item.dateTime);
        if (!itemTime.isBetween(startOfDay, endOfDay, null, '[]')) {
          return false;
        }
      }
      return true;
    });
  }, [userActionsData, filters]);

  const uniqueOptions = useMemo(() => {
    const result = { username: [], event: [], caseCode: [] };
    userActionsData.forEach(item => {
      ['username', 'event', 'caseCode'].forEach(key => {
        const value = item[key];
        if (value !== null && value !== undefined && value !== '' && !result[key].includes(value)) {
          result[key].push(value);
        }
      });
    });
    return result;
  }, [userActionsData]);

  const applyFilter = useCallback((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const resetFilter = useCallback(name => {
    setFilters(prev => ({ ...prev, [name]: [] }));
  }, []);

  const getColumnFilterProps = name => ({
    filterDropdown: props => {
      if (name === 'dateTime') {
        return (
          <DateRangeFilter
            {...props}
            onApply={range => applyFilter('dateTime', range)}
            onReset={() => resetFilter('dateTime')}
          />
        );
      }
      const getLabel = name === 'event' ? value => (Events[value] ? t(Events[value]) : value) : null;
      return (
        <CheckboxFilter
          {...props}
          allOptions={uniqueOptions[name] || []}
          getLabel={getLabel}
          onApply={keys => applyFilter(name, keys)}
          onReset={() => resetFilter(name)}
        />
      );
    },
    filteredValue: filters[name] && filters[name].length ? filters[name] : null,
    onFilter: () => true,
  });

  const COLUMNS = [
    {
      title: '№',
      dataIndex: 'key',
      align: 'center',
    },
    {
      title: t('userActions.login'),
      dataIndex: 'username',
      ...getColumnFilterProps('username'),
    },
    {
      title: t('userActions.dateTime'),
      dataIndex: 'dateTime',
      render: record => (record ? moment(record).format('DD.MM.YYYY HH:mm:ss') : '—'),
      ...getColumnFilterProps('dateTime'),
    },
    {
      title: t('userActions.action'),
      dataIndex: 'event',
      render: record => (record && Events[record] ? t(Events[record]) : record || '—'),
      ...getColumnFilterProps('event'),
    },
    {
      title: t('userActions.caseName'),
      dataIndex: 'caseCode',
      render: record => record || '—',
      ...getColumnFilterProps('caseCode'),
    },
    {
      title: t('userActions.object'),
      dataIndex: 'object',
      render: record =>
        record
          ? Object.keys(record).map(key => (
              <p className={styles.actionObject}>
                <span>{t(EventObject[key])}:</span> {record[key]}
              </p>
            ))
          : '—',
    },
  ];

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const roleList = checkRole();
    const result = roleList.filter(role => roles.includes(role));

    setIsAction(result.length);
  }, []);

  return (
    <BasicLayout>
      {isAction ? (
        <div className={styles.actions}>
          <Spin tip={t('intersections.loadingPleaseWait') + '...'} spinning={isLoading}>
            <Table
              columns={COLUMNS}
              dataSource={filteredData.map((data, index) => ({ ...data, key: index + 1 }))}
              bordered
              pagination={{
                defaultPageSize: 50,
                pageSizeOptions: ['50', '100', '200', '500'],
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}–${range[1]} из ${total}`,
              }}
            />
          </Spin>
        </div>
      ) : (
        <div className={styles.errorTitle}>
          <h1>{t('error.doNotHaveAccessToViewThisModule')}</h1>
        </div>
      )}
    </BasicLayout>
  );
};
